import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Wallet,
  Coins,
  ExternalLink,
  CheckCircle,
  ArrowRight,
  Twitter,
  Rocket,
  Zap,
  Shield,
  Users,
  Lock,
  Brain,
  Copy,
  ChevronRight,
  Check
} from 'lucide-react';
import {
  heroData,
  aboutData,
  whyBuyData,
  socialProofData,
  howToBuyData,
  memeData,
  roadmapData,
  footerData
} from '../data/mock';

// Gold accent color
const ACCENT = "#FFC107";
const ACCENT_HOVER = "rgba(255, 193, 7, 0.1)";

// Copy to clipboard hook
const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false);
  
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return { copied, copy };
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a 
          href="https://pump.fun/profile/work2moon"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src={heroData.logo} 
            alt="FOCUS Logo" 
            className="w-10 h-10 object-contain"
          />
          <span className="text-white text-xl font-semibold tracking-tight">$FOCUS</span>
        </a>
        
        <nav className="hidden md:flex items-center gap-8">
          <a href="#about" className="text-[#4D4D4D] hover:text-white transition-colors text-lg">About</a>
          <a href="#why" className="text-[#4D4D4D] hover:text-white transition-colors text-lg">Why Buy</a>
          <a href="#how" className="text-[#4D4D4D] hover:text-white transition-colors text-lg">How to Buy</a>
          <a href="#roadmap" className="text-[#4D4D4D] hover:text-white transition-colors text-lg">Roadmap</a>
          <a 
            href="https://pump.fun/profile/work2moon" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#4D4D4D] hover:text-white transition-colors text-lg"
          >
            Creator
          </a>
        </nav>

        <a
          href={heroData.ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 py-3 font-medium text-lg transition-all duration-300"
          style={{ backgroundColor: ACCENT, color: '#000' }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = ACCENT_HOVER;
            e.target.style.color = ACCENT;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = ACCENT;
            e.target.style.color = '#000';
          }}
        >
          Buy Now
        </a>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroData.backgroundImage})` }}
      />
      <div className="absolute inset-0 bg-black/80" />
      
      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `
            repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%),
            repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)
          `,
          backgroundSize: '100% 100%'
        }}
      />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <div 
            className="relative w-44 h-44 md:w-56 md:h-56 rounded-full overflow-hidden"
            style={{ 
              boxShadow: '0 0 60px rgba(255,193,7,0.4), 0 0 100px rgba(255,193,7,0.2)',
              background: 'radial-gradient(circle, rgba(0,0,0,0.9) 0%, rgba(0,0,0,1) 70%)'
            }}
          >
            <img 
              src={heroData.logo} 
              alt="FOCUS Logo" 
              className="w-full h-full object-cover scale-125"
              style={{ 
                mixBlendMode: 'lighten'
              }}
            />
          </div>
        </div>

        {/* Ticker */}
        <Badge 
          className="mb-6 bg-white/10 text-lg px-4 py-2 hover:bg-white/10 border"
          style={{ color: ACCENT, borderColor: `${ACCENT}40` }}
        >
          {heroData.ticker} on Solana
        </Badge>

        {/* Tagline */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight leading-tight">
          {heroData.tagline}
        </h1>

        {/* Subtitle */}
        <p className="text-xl md:text-2xl text-white/70 mb-12 max-w-2xl mx-auto">
          The meme coin for builders who execute, not excuse.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href={heroData.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group px-8 py-4 font-semibold text-xl transition-all duration-400 flex items-center gap-3 min-w-[250px] justify-center border-2"
            style={{ 
              backgroundColor: ACCENT, 
              color: '#000',
              borderColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = ACCENT_HOVER;
              e.target.style.color = ACCENT;
              e.target.style.borderColor = ACCENT;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = ACCENT;
              e.target.style.color = '#000';
              e.target.style.borderColor = 'transparent';
            }}
          >
            {heroData.ctaText}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          
          <a
            href="#about"
            className="bg-white/10 text-white px-8 py-4 font-semibold text-xl hover:bg-white hover:text-black transition-all duration-400 flex items-center gap-3 min-w-[250px] justify-center"
          >
            Learn More
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 rounded-full" style={{ backgroundColor: ACCENT }} />
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-32 bg-black relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <Badge 
              className="mb-6 hover:bg-opacity-10 border"
              style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
            >
              About
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
              {aboutData.title}
            </h2>
            <div className="space-y-6">
              {aboutData.description.map((text, index) => (
                <p key={index} className="text-lg text-white/70 leading-relaxed">
                  {text}
                </p>
              ))}
            </div>
            
            <div className="mt-10 flex gap-6">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}15` }}
                >
                  <Shield className="w-6 h-6" style={{ color: ACCENT }} />
                </div>
                <div>
                  <div className="text-white font-semibold">Safe</div>
                  <div className="text-white/50 text-sm">LP Burned</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}15` }}
                >
                  <Users className="w-6 h-6" style={{ color: ACCENT }} />
                </div>
                <div>
                  <div className="text-white font-semibold">Community</div>
                  <div className="text-white/50 text-sm">Driven</div>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="aspect-square relative overflow-hidden">
              <img 
                src={aboutData.image} 
                alt="Focus" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
            </div>
            {/* Floating Badge */}
            <div 
              className="absolute -bottom-6 -left-6 p-6"
              style={{ backgroundColor: ACCENT }}
            >
              <div className="text-black text-3xl font-bold">100%</div>
              <div className="text-black/70 text-sm">Fair Launch</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Buy Section
const WhyBuySection = () => {
  const icons = [Lock, Brain, Zap, Users];

  return (
    <section id="why" className="py-32 bg-[#0a0a0a] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <Badge 
            className="mb-6 hover:bg-opacity-10 border"
            style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
          >
            Why $FOCUS
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {whyBuyData.title}
          </h2>
          <p className="text-xl text-white/50 max-w-2xl mx-auto">
            Built for the disciplined. Held by the focused.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {whyBuyData.points.map((point, index) => {
            const Icon = icons[index];
            return (
              <Card 
                key={index} 
                className="bg-black border border-white/10 hover:border-opacity-50 transition-all duration-400 group"
                style={{ '--hover-border': `${ACCENT}80` }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = `${ACCENT}80`}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div 
                      className="w-14 h-14 flex items-center justify-center shrink-0 transition-colors duration-400 group-hover:bg-opacity-100"
                      style={{ backgroundColor: `${ACCENT}15` }}
                    >
                      <Icon className="w-7 h-7 transition-colors duration-400" style={{ color: ACCENT }} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {point.title}
                      </h3>
                      <p className="text-white/60">
                        {point.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  return (
    <section className="py-32 bg-black relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <Badge 
            className="mb-6 hover:bg-opacity-10 border"
            style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
          >
            Community
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {socialProofData.title}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {socialProofData.tweets.map((tweet, index) => (
            <a 
              key={index}
              href={tweet.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card 
                className="bg-[#0a0a0a] border border-white/10 hover:border-opacity-30 transition-all duration-400 h-full cursor-pointer group"
                onMouseEnter={(e) => e.currentTarget.style.borderColor = `${ACCENT}50`}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-black"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {tweet.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium text-sm truncate">{tweet.author}</div>
                      <div className="text-white/40 text-xs">@{tweet.handle}</div>
                    </div>
                    <Twitter className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                  </div>
                  <p className="text-white/80 text-lg leading-relaxed">
                    {tweet.content}
                  </p>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// How to Buy Section
const HowToBuySection = () => {
  const { copied, copy } = useCopyToClipboard();
  const iconMap = {
    wallet: Wallet,
    coins: Coins,
    'external-link': ExternalLink,
    'check-circle': CheckCircle
  };

  const profileUrl = heroData.profileUrl;

  return (
    <section id="how" className="py-32 bg-[#0a0a0a] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <Badge 
            className="mb-6 hover:bg-opacity-10 border"
            style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
          >
            Get Started
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {howToBuyData.title}
          </h2>
          <p className="text-xl text-white/50 max-w-2xl mx-auto">
            Four simple steps to join the focused.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {howToBuyData.steps.map((step, index) => {
            const Icon = iconMap[step.icon];
            const CardWrapper = step.link ? 'a' : 'div';
            const cardProps = step.link ? {
              href: step.link,
              target: "_blank",
              rel: "noopener noreferrer"
            } : {};
            
            return (
              <div 
                key={index} 
                className="relative group"
              >
                {/* Connector Line */}
                {index < howToBuyData.steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-full w-full h-[2px] bg-white/10 z-0" />
                )}
                
                <CardWrapper {...cardProps} className={step.link ? 'block h-full' : 'h-full'}>
                  <Card 
                    className={`bg-black border border-white/10 hover:border-opacity-50 transition-all duration-400 relative z-10 h-full ${step.link ? 'cursor-pointer' : ''}`}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = `${ACCENT}80`}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                  >
                    <CardContent className="p-6 text-center">
                      {/* Step Number */}
                      <div 
                        className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 flex items-center justify-center text-black font-bold"
                        style={{ backgroundColor: ACCENT }}
                      >
                        {step.step}
                      </div>
                      
                      <div 
                        className="w-16 h-16 mx-auto flex items-center justify-center mt-4 mb-4 transition-colors duration-400"
                        style={{ backgroundColor: `${ACCENT}15` }}
                      >
                        <Icon className="w-8 h-8 transition-colors duration-400" style={{ color: ACCENT }} />
                      </div>
                      
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {step.title}
                      </h3>
                      <p className="text-white/50 text-sm">
                        {step.description}
                      </p>
                      {step.link && (
                        <div className="mt-3 flex items-center justify-center gap-1 text-xs" style={{ color: ACCENT }}>
                          <ExternalLink className="w-3 h-3" />
                          <span>Click to open</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </CardWrapper>
              </div>
            );
          })}
        </div>

        {/* Profile Link */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-black border border-white/10 px-6 py-4">
            <span className="text-white/50">Creator Profile:</span>
            <a 
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs sm:text-sm break-all hover:underline" 
              style={{ color: ACCENT }}
            >
              pump.fun/profile/work2moon
            </a>
            <button 
              onClick={() => copy(profileUrl)}
              className="flex items-center gap-2 px-3 py-1.5 transition-all duration-300 hover:opacity-80"
              style={{ backgroundColor: copied ? '#22c55e' : `${ACCENT}20`, color: copied ? '#fff' : ACCENT }}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span className="text-sm">Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Meme Section
const MemeSection = () => {
  return (
    <section className="py-32 bg-black relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <Badge 
            className="mb-6 hover:bg-opacity-10 border"
            style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
          >
            Viral Zone
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {memeData.title}
          </h2>
          <p className="text-xl text-white/50">
            {memeData.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {memeData.memes.map((meme, index) => (
            <div 
              key={index} 
              className="relative aspect-square overflow-hidden group cursor-pointer"
            >
              <img 
                src={meme.image} 
                alt={meme.overlayText}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-colors duration-400" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl md:text-4xl font-bold text-white text-center px-4 tracking-wide">
                  {meme.overlayText}
                </span>
              </div>
              {/* Share prompt on hover */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                <span 
                  className="text-black px-4 py-2 text-sm font-medium"
                  style={{ backgroundColor: ACCENT }}
                >
                  Click to Share
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Roadmap Section
const RoadmapSection = () => {
  return (
    <section id="roadmap" className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background Glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[150px]"
        style={{ backgroundColor: `${ACCENT}08` }}
      />
      
      <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-16">
          <Badge 
            className="mb-6 hover:bg-opacity-10 border"
            style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, borderColor: `${ACCENT}40` }}
          >
            The Journey
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {roadmapData.title}
          </h2>
          <p className="text-2xl font-medium" style={{ color: ACCENT }}>
            {roadmapData.teaser}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {roadmapData.upcomingCoins.map((coin, index) => (
            <Card 
              key={index} 
              className={`border transition-all duration-400 ${
                index === 0 
                  ? 'bg-opacity-10' 
                  : 'bg-black/50 border-white/10 hover:border-white/30'
              }`}
              style={index === 0 ? { backgroundColor: `${ACCENT}15`, borderColor: `${ACCENT}80` } : {}}
            >
              <CardContent className="p-8 text-center">
                <div className="text-3xl font-bold text-white mb-2">
                  {coin.name}
                </div>
                <Badge 
                  className={index === 0 ? 'text-black' : 'bg-white/10 text-white/70'}
                  style={index === 0 ? { backgroundColor: ACCENT } : {}}
                >
                  {coin.status}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/50 text-lg">
            The ecosystem grows. Stay focused.
          </p>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-32 bg-black relative">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
          Ready to <span style={{ color: ACCENT }}>Lock In</span>?
        </h2>
        <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
          Join thousands of focused builders. No more distractions. Just execution.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={heroData.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group px-10 py-5 font-semibold text-xl transition-all duration-400 flex items-center gap-3 justify-center border-2"
            style={{ 
              backgroundColor: ACCENT, 
              color: '#000',
              borderColor: 'transparent',
              boxShadow: `0 0 30px ${ACCENT}50`
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = ACCENT_HOVER;
              e.target.style.color = ACCENT;
              e.target.style.borderColor = ACCENT;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = ACCENT;
              e.target.style.color = '#000';
              e.target.style.borderColor = 'transparent';
            }}
          >
            Buy $FOCUS Now
            <Rocket className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const iconMap = {
    twitter: Twitter,
    rocket: Rocket
  };

  return (
    <footer className="py-16 bg-[#050505] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img 
              src={heroData.logo} 
              alt="FOCUS Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-white text-lg font-medium">{footerData.brand}</span>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {footerData.socials.map((social, index) => {
              const Icon = iconMap[social.icon];
              return (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/5 flex items-center justify-center transition-all duration-400 group"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ACCENT}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                >
                  <Icon className="w-5 h-5 text-white/70 group-hover:text-black transition-colors" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t border-white/5 text-center">
          <p className="text-white/30 text-sm max-w-2xl mx-auto">
            {footerData.disclaimer}
          </p>
          <p className="text-white/20 text-xs mt-4">
            © 2025 $FOCUS. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="bg-black min-h-screen">
      <Header />
      <HeroSection />
      <AboutSection />
      <WhyBuySection />
      <SocialProofSection />
      <HowToBuySection />
      <MemeSection />
      <RoadmapSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
